// The browser's half of personalities (lib/identity): reading the person's
// personalities and the lists each agent, connection and key mixes, and saving
// them so that a save that does not land is never a sentence in front of the
// person (John, 2026-10-25: "anything that fails needs to automatically compile
// back up and should just be fixed on the fly").
//
// How a save heals: every save is idempotent (a whole identity list merged on the
// server, or one mix list written whole), so a failed one is simply sent again,
// with a growing pause, until it lands. A mix list that could not be written is
// also kept in this browser (`pending`), so a page closed before it landed
// writes it on the next load of any place that reads personalities. A target
// that is gone (404) lets its record go. Nothing here ever throws.

import type { AgentIdentity, IdentityMix } from "./types";

export interface MixPlaceRow {
  kind: "agent" | "connection" | "key";
  name: string;
  status?: string;
  slug?: string;
}

export interface MixState {
  /** false while migration 164 is not applied: nothing is mixed yet, and a save waits */
  ready: boolean;
  mix: IdentityMix;
  places: Record<string, MixPlaceRow>;
}

/** the person's personalities: `open` false when personalities are not open to them (the routes answer 404) */
export async function getPersonalities(): Promise<{ open: boolean; list: AgentIdentity[] } | null> {
  try {
    const r = await fetch("/api/me/agent-identities", { cache: "no-store" });
    if (r.status === 404 || r.status === 401) return { open: false, list: [] };
    if (!r.ok) return null;
    return { open: true, list: (((await r.json()) as { identities?: AgentIdentity[] }).identities ?? []) };
  } catch {
    return null;
  }
}

export async function getMix(): Promise<MixState | null> {
  try {
    const r = await fetch("/api/me/identity-mix", { cache: "no-store" });
    if (!r.ok) return null;
    const j = (await r.json()) as Partial<MixState>;
    return { ready: j.ready !== false, mix: j.mix ?? {}, places: j.places ?? {} };
  } catch {
    return null;
  }
}

/** one try: 'saved', 'gone' (the target is not there any more), or 'later' */
async function putOnce(body: { target: string; ids?: string[]; from?: string }): Promise<"saved" | "gone" | "later"> {
  try {
    const r = await fetch("/api/me/identity-mix", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (r.ok) return "saved";
    if (r.status === 404 || r.status === 400 || r.status === 401) return "gone";
    return "later";
  } catch {
    return "later";
  }
}

// ── what waits to be written, in this browser ────────────────────────────────

const PENDING_KEY = "innernet:identity-mix:pending";
const PENDING_DAYS = 14;
type Pending = Record<string, { ids: string[]; at: number }>;

function readPending(): Pending {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    const p = raw ? (JSON.parse(raw) as Pending) : {};
    return p && typeof p === "object" ? p : {};
  } catch {
    return {};
  }
}
function writePending(p: Pending) {
  try {
    if (Object.keys(p).length) window.localStorage.setItem(PENDING_KEY, JSON.stringify(p));
    else window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* a storage that will not keep it only means the page's own retries are all there is */
  }
}
/** keep a list to write, for when this page is gone before it lands */
export function recordPendingMix(target: string, ids: readonly string[]) {
  const p = readPending();
  p[target] = { ids: [...ids], at: Date.now() };
  writePending(p);
}
function clearPending(target: string) {
  const p = readPending();
  if (!(target in p)) return;
  delete p[target];
  writePending(p);
}

// ── saving quietly ───────────────────────────────────────────────────────────

const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** the pause before the next try: 1.5s, 3s, 6s … up to a minute */
export const backoff = (attempt: number) => Math.min(60_000, 1500 * 2 ** Math.max(0, attempt));

/**
 * Write one list, and keep writing it until it lands. The newest choice for a
 * target always wins: a later save replaces the one still trying. Resolves when
 * it landed (true) or the target is gone (false); it never rejects.
 */
export function saveMixQuietly(target: string, ids: readonly string[]): Promise<boolean> {
  recordPendingMix(target, ids);
  const prev = timers.get(target);
  if (prev) clearTimeout(prev);
  return new Promise((resolve) => {
    let attempt = 0;
    const go = async () => {
      timers.delete(target);
      // a newer choice for this target took over: this one stands down
      const now = readPending()[target];
      if (now && now.ids.join(",") !== ids.join(",")) return resolve(false);
      const out = await putOnce({ target, ids: [...ids] });
      if (out !== "later") {
        clearPending(target);
        return resolve(out === "saved");
      }
      timers.set(target, setTimeout(go, backoff(attempt++)));
    };
    void go();
  });
}

/** Copy one target's list onto another (a connection whose first call arrived as a different row). Tried until it lands. */
export async function copyMixQuietly(target: string, from: string, tries = 4): Promise<boolean> {
  for (let attempt = 0; attempt < tries; attempt++) {
    const out = await putOnce({ target, from });
    if (out !== "later") return out === "saved";
    await new Promise((r) => setTimeout(r, backoff(attempt)));
  }
  return false;
}

/** Write what an earlier page could not (once per page load; old records are let go). */
let applied = false;
export async function applyPendingMixes(): Promise<void> {
  if (applied || typeof window === "undefined") return;
  applied = true;
  const p = readPending();
  const fresh = Date.now() - PENDING_DAYS * 86_400_000;
  for (const [target, rec] of Object.entries(p)) {
    if (!rec || rec.at < fresh || !Array.isArray(rec.ids)) {
      clearPending(target);
      continue;
    }
    void saveMixQuietly(target, rec.ids);
  }
}

/**
 * Save the whole list of personalities (a PUT the server merges, mergeSave), and
 * keep saving it until it lands. `send` is the page's own (it knows what it has
 * been shown); this only decides when to try again.
 */
export function retryQuietly<T>(send: () => Promise<T>, o: { onLanded?: (v: T) => void; max?: number } = {}): { cancel: () => void } {
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const go = async () => {
    if (stopped) return;
    try {
      const v = await send();
      if (!stopped) o.onLanded?.(v);
    } catch {
      if (stopped || attempt >= (o.max ?? Infinity)) return;
      timer = setTimeout(go, backoff(attempt++));
    }
  };
  void go();
  return {
    cancel: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  };
}
